import type { PortfolioStatementImportV2 } from "@tradvera/contracts";
import { statementImportWriteOutcomeUnknownV2 } from "./client";
import type { StatementWriteRecoveryV2 } from "./durable-write";

export type StatementImportUploadAttemptV2 = { ok: true }
  | { ok: false; errorCode: string };

export function rememberUploadedImportV2(target: PortfolioStatementImportV2[],
  imported: PortfolioStatementImportV2): void {
  const existing = target.findIndex((item) => item.importId === imported.importId);
  if (existing >= 0) target[existing] = imported;
  else target.push(imported);
}

/** Only an indeterminate network/server outcome may be recovered after an upload
 * attempt. Deterministic browser and preflight failures remain visible. */
export function shouldRecoverStatementUploadFailureV2(error: unknown): boolean {
  return statementImportWriteOutcomeUnknownV2(error);
}

/** Resolve one file completely before advancing the five-file bundle. A lost or
 * contract-invalid response that is proven by bound server status/detail is success,
 * not a reason to abandon the remaining files. */
export async function executeRecoverableStatementUploadFileV2(options: {
  execute(): Promise<PortfolioStatementImportV2>;
  recover(): Promise<StatementWriteRecoveryV2>;
  onFailure?(error: unknown): void;
}): Promise<PortfolioStatementImportV2> {
  let exactRetryUsed = false;
  for (;;) {
    try {
      return await options.execute();
    } catch (error) {
      options.onFailure?.(error);
      if (!shouldRecoverStatementUploadFailureV2(error)) throw error;
      const truth = await options.recover();
      if (truth.state === "RECOVERED" && truth.marker.operation === "UPLOAD") {
        return truth.import;
      }
      if (truth.state === "RETRY_ALLOWED" && truth.marker.operation === "UPLOAD"
        && !exactRetryUsed) {
        exactRetryUsed = true;
        continue;
      }
      throw new Error(recoveryFailureCode(truth, error));
    }
  }
}

function recoveryFailureCode(truth: StatementWriteRecoveryV2, original: unknown): string {
  if (truth.state === "LOCKED") {
    return truth.code === "OUTCOME_UNKNOWN" ? "STATEMENT_IMPORT_OUTCOME_UNKNOWN" : truth.code;
  }
  if (truth.state === "PENDING") return "STATEMENT_IMPORT_PENDING_OPERATION_PENDING";
  if (truth.state === "RETRY_ALLOWED") return "STATEMENT_IMPORT_EXACT_OPERATION_RETRY_FAILED";
  if (truth.state === "RECOVERED") return "STATEMENT_IMPORT_PENDING_OPERATION_OTHER_OPERATION";
  if (statementImportWriteOutcomeUnknownV2(original)) return "STATEMENT_IMPORT_OUTCOME_UNKNOWN";
  return original instanceof Error && original.message
    ? original.message : "STATEMENT_IMPORT_RESPONSE_INVALID";
}
